import * as React from "react";
import { homeLink, request } from "../../../../../services/cpln";
import { Button, Card, Space, Tooltip } from "antd";
import { CardTitle } from "../../../../../components/antd/card/CardTitle";
import { CardContent } from "../../../../../components/antd/card/CardContent";
import { CardCollapsibleContent } from "../../../../../components/antd/card/CardCollapsibleContent";
import { CaretRightOutlined, EllipsisOutlined, PlusOutlined } from "@ant-design/icons";
import { v4 as uuidv4 } from "uuid";
import { NGProviderBrowser } from "../../../ngProviderBrowser";
import { inputToMk8s } from "../../../draft/utils/inputToMk8s";
import { ProviderPreInstallScript } from "../../../advanced/providerPreInstallScript";
import { ProviderAutoscaler } from "../../../advanced/providerAutoscaler";
import { ProviderNetworking } from "../../../advanced/providerNetworking";
import { formSetValue, formTriggerValidation, useCpFormValue } from "../../../../gvc/testNewForm/utils";
import { CpInput } from "../../../../gvc/testNewForm/CpInput";
import { CpFormItem } from "../../../../gvc/testNewForm/CpFormItem";
import { CpLabel } from "../../../../gvc/testNewForm/CpLabel";
import { CpErrorWarning } from "../../../../gvc/testNewForm/CpErrorWarning";
import { CpArrayString } from "../../../../gvc/testNewForm/CpArrayString";
import { CpArrayItemNameValuePair } from "../../../../workload/draft/CpArrayItemNameValuePair";
import { CpArrayObject } from "../../../../gvc/testNewForm/CpArrayObject";
import { store } from "../../../../../store";
import { Mk8sInput } from "../../../../../schema/types/mk8s/input/mk8s";
import { cloneDeep } from "lodash";
import { useCpFormContext } from "../../../../../components/form/useCpFormContext";
import { CpErrorWarningArrayIcon } from "../../../../gvc/testNewForm/CpErrorWarningArrayIcon";
import { CpDirtyIndicatorArrayIcon } from "../../../../gvc/testNewForm/CpDirtyIndicatorArrayIconLink";
import { CpKindSelect } from "../../../../gvc/testNewForm/temp/CpKindSelect";
import { useDrawerContext } from "../../../../../components/antd/drawerContext";
import { EmbeddedSecretCreate } from "../../../../secret/create/embedded";
import { parseLink } from "../../../../../utils/linkParser/linkParser";
import { CpLabelTooltip } from "../../../../gvc/testNewForm/CpLabelTooltip";
import { CpTooltip } from "../../../../gvc/testNewForm/CpTooltip";

interface Props {}
export const DigitalOceanProviderSelf: React.FC<Props> = ({}) => {
  const { id: formId } = useCpFormContext();

  const drawer = useDrawerContext();

  const [browserKey, setBrowserKey] = React.useState("");
  const [isAdvancedCollapsed, setIsAdvancedCollapsed] = React.useState(true);

  const region = useCpFormValue(`digitalocean.region`);
  const vpcId = useCpFormValue(`digitalocean.vpcId`);
  const image = useCpFormValue(`digitalocean.image`);
  const sshKeys = useCpFormValue(`digitalocean.sshKeys`);
  const extraSshKeys = useCpFormValue(`digitalocean.extraSshKeys`);
  const reservedIps = useCpFormValue(`digitalocean.reservedIPs`);

  return (
    <>
      <div className="cpln-form-section cpln-ant-card-container">
        <Card>
          <CardTitle>
            Digital Ocean Provider
            <CpErrorWarningArrayIcon
              names={[
                `digitalocean.region`,
                `digitalocean.digitalOceanTags`,
                `digitalocean.tokenSecretLink`,
                `digitalocean.vpcId`,
                `digitalocean.image`,
                `digitalocean.sshKeys`,
                `digitalocean.extraSshKeys`,
                `digitalocean.reservedIPs`,
                `digitalocean.autoscaler`,
                `digitalocean.networking`,
                `digitalocean.preInstallScript`,
              ]}
            />
          </CardTitle>
          <CardContent>
            <CpFormItem>
              <CpLabel name={`digitalocean.region`}>Region</CpLabel>
              <Space.Compact>
                <CpInput name={`digitalocean.region`} style={{ width: 600 }} />
                <Tooltip title={`Browse`}>
                  <Button icon={<EllipsisOutlined />} onClick={() => setBrowserKey("regions")} />
                </Tooltip>
              </Space.Compact>
              <CpErrorWarning name={`digitalocean.region`} />
            </CpFormItem>
            <CpFormItem>
              <CpLabelTooltip>
                <CpLabel name={`digitalocean.tokenSecretLink`}>Digital Ocean Token Secret</CpLabel>
                <CpTooltip
                  tooltipProps={{
                    title: `An opaque secret that contains your Digital Ocean token, it will be used to manage servers in your Digital Ocean account`,
                  }}
                />
              </CpLabelTooltip>
              <Space.Compact>
                <CpKindSelect
                  kind="secret"
                  name={`digitalocean.tokenSecretLink`}
                  optionValue="name"
                  queries={[{ property: "type", value: "opaque" }]}
                  style={{ width: 600 }}
                />
                <Tooltip title={`Create an Opaque Secret`}>
                  <Button
                    onClick={() => {
                      drawer.setPersistent(
                        `mk8s-digitalocean-create-secret`,
                        "Create Opaque Secret",
                        <>
                          <EmbeddedSecretCreate
                            onCancel={() => drawer.closePersistent()}
                            onDone={(itemLink) => {
                              drawer.closePersistent();
                              const { name } = parseLink(itemLink, { useInputCtx: true });
                              const secretState = {
                                value: name,
                                name: name,
                                link: itemLink,
                              };
                              formSetValue(formId, `digitalocean.tokenSecretLink`, secretState);
                              formTriggerValidation(formId);
                            }}
                            lockedType={`opaque`}
                          />
                        </>,
                        600,
                      );
                    }}
                    icon={<PlusOutlined />}
                  />
                </Tooltip>
              </Space.Compact>
              <CpErrorWarning name={`digitalocean.tokenSecretLink`} />
            </CpFormItem>
            <CpFormItem>
              <CpLabel name={`digitalocean.vpcId`}>VPC ID</CpLabel>
              <Space.Compact>
                <CpInput name={`digitalocean.vpcId`} style={{ width: 600 }} />
                <Tooltip title={`Browse`}>
                  <Button icon={<EllipsisOutlined />} onClick={() => setBrowserKey("vpcId")} />
                </Tooltip>
              </Space.Compact>
              <CpErrorWarning name={`digitalocean.vpcId`} />
            </CpFormItem>
            <CpFormItem>
              <CpLabel name={`digitalocean.image`}>Image</CpLabel>
              <Space.Compact>
                <CpInput name={`digitalocean.image`} style={{ width: 600 }} />
                <Tooltip title={`Browse`}>
                  <Button icon={<EllipsisOutlined />} onClick={() => setBrowserKey("image")} />
                </Tooltip>
              </Space.Compact>
              <CpErrorWarning name={`digitalocean.image`} />
            </CpFormItem>
            <div style={{ maxWidth: 600 }}>
              <CpFormItem>
                <div className="flex items-center gap-4">
                  <CpLabel name="skip">SSH Keys</CpLabel>
                  <Button className="ml-1" size={"small"} type={"default"} onClick={() => setBrowserKey("ssh-keys")}>
                    Browse
                  </Button>
                </div>
                <div className="-mt-2">
                  <CpArrayString name="digitalocean.sshKeys" />
                </div>
              </CpFormItem>
            </div>
            <div className="mt-4">
              <CardTitle style={{ marginBottom: 0, fontSize: 20 }}>
                <div className={`flex items-baseline ${isAdvancedCollapsed ? "" : "mb-4"}`}>
                  <div
                    className={`flex items-center gap-2 color-link-hover cursor-pointer`}
                    onClick={() => {
                      setIsAdvancedCollapsed((x) => !x);
                    }}
                  >
                    <CaretRightOutlined rotate={isAdvancedCollapsed ? 0 : 90} className={`self-center`} />
                    <span>Advanced</span>
                    <CpDirtyIndicatorArrayIcon names={["provider-digitalocean-advanced"]} />
                  </div>
                </div>
              </CardTitle>
            </div>
            <CardCollapsibleContent collapsed={isAdvancedCollapsed}>
              <div style={{ maxWidth: 600 }}>
                <CpFormItem>
                  <div className="flex items-center gap-4">
                    <CpLabel name="skip">Extra SSH Keys</CpLabel>
                    <Button
                      className="ml-1"
                      size={"small"}
                      type={"default"}
                      onClick={() => setBrowserKey("extra-ssh-keys")}
                    >
                      Browse
                    </Button>
                  </div>
                  <div className="-mt-2">
                    <CpArrayString name="digitalocean.extraSshKeys" />
                  </div>
                </CpFormItem>
              </div>
              <div style={{ maxWidth: 900 }}>
                <CpArrayObject
                  factoryFn={() => ({ id: uuidv4(), name: "", value: "" })}
                  name={`digitalocean.digitalOceanTags`}
                  label="Digital Ocean Tags"
                  labels={["Key", "Value"]}
                >
                  <CpArrayItemNameValuePair />
                </CpArrayObject>
              </div>
              <div style={{ maxWidth: 600 }}>
                <CpFormItem>
                  <div className="flex items-center gap-4">
                    <CpLabel name="skip">Reserved IPs</CpLabel>
                    <Button
                      className="ml-1"
                      size={"small"}
                      type={"default"}
                      onClick={() => setBrowserKey("reservedIps")}
                    >
                      Browse
                    </Button>
                  </div>
                  <div className="-mt-2">
                    <CpArrayString name={`digitalocean.reservedIPs`} />
                  </div>
                </CpFormItem>
              </div>
              <ProviderPreInstallScript provider="digitalocean" />
              <ProviderNetworking provider="digitalocean" />
              <ProviderAutoscaler provider="digitalocean" />
            </CardCollapsibleContent>
          </CardContent>
        </Card>
      </div>
      {browserKey === "regions" ? (
        <NGProviderBrowser
          title="Browse Digital Ocean Regions"
          initialValue={region}
          fetchData={async () => {
            const form = store.getState().form.instances[formId];
            const values: Mk8sInput = form.values as any;

            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "regions",
                cluster: inputToMk8s(values),
              },
            });
            return data.regions;
          }}
          onOk={(value) => {
            formSetValue(formId, `digitalocean.region`, value as string);
            formTriggerValidation(formId);
          }}
          onClose={() => setBrowserKey("")}
          labels={["Name", "Slug"]}
          getData={(item: any) => [item.slug, item.name, item.slug]}
        />
      ) : null}
      {browserKey === "vpcId" ? (
        <NGProviderBrowser
          title="Browse Digital Ocean VPCs"
          initialValue={vpcId}
          fetchData={async () => {
            const form = store.getState().form.instances[formId];
            const values: Mk8sInput = form.values as any;

            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "vpcs",
                cluster: inputToMk8s(values),
              },
            });
            return data.vpcs;
          }}
          onOk={(value) => {
            formSetValue(formId, `digitalocean.vpcId`, value as string);
            formTriggerValidation(formId);
          }}
          onClose={() => setBrowserKey("")}
          labels={["Name", "ID", "Description", "IP Range"]}
          getData={(item: any) => [String(item.id), item.name, String(item.id), item.description, item.ip_range]}
        />
      ) : null}
      {browserKey === "image" ? (
        <NGProviderBrowser
          title="Browse Digital Ocean Images"
          initialValue={image}
          fetchData={async () => {
            const form = store.getState().form.instances[formId];
            const values: Mk8sInput = form.values as any;

            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "images",
                cluster: inputToMk8s(values),
              },
            });
            return data.images.sort((a: any, b: any) => {
              if (a.slug > b.slug) {
                return 1;
              }
              if (a.slug < b.slug) {
                return -1;
              }
              return 0;
            });
          }}
          onOk={(value) => {
            formSetValue(formId, `digitalocean.image`, value as string);
            formTriggerValidation(formId);
          }}
          onClose={() => setBrowserKey("")}
          labels={["Name", "Availability", "Slug"]}
          tooltips={[
            {
              label: "Availability",
              content: `Shows if the image is available in selected region (${region})`,
            },
          ]}
          getData={(item: any) => {
            const available = item.regions.includes(region);

            return [item.slug, item.description, [available, available ? "Available" : "Not Available"], item.slug];
          }}
        />
      ) : null}
      {browserKey === "ssh-keys" ? (
        <NGProviderBrowser
          title="Browse Digital Ocean SSH Keys"
          initialValue={sshKeys.map((i) => i.value)}
          fetchData={async () => {
            const form = store.getState().form.instances[formId];
            const values: Mk8sInput = form.values as any;

            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "ssh_keys",
                cluster: inputToMk8s(values),
              },
            });
            return data.ssh_keys;
          }}
          multipleChoice
          onOk={(_value) => {
            const values: string[] = _value as any;
            const form = store.getState().form.instances[formId];
            const formValues: Mk8sInput = form.values as any;
            const _sshKeys = cloneDeep(formValues.digitalocean.sshKeys);

            for (const value of values) {
              if (_sshKeys.some((i) => i.value === value)) {
                continue;
              }
              _sshKeys.push({ id: uuidv4(), value: value });
            }
            formSetValue(formId, `digitalocean.sshKeys`, _sshKeys);
            formTriggerValidation(formId);
          }}
          onClose={() => setBrowserKey("")}
          labels={["Name", "ID"]}
          getData={(item: any) => [String(item.id), item.name, String(item.id)]}
        />
      ) : null}
      {browserKey === "extra-ssh-keys" ? (
        <NGProviderBrowser
          title="Browse Digital Ocean Extra SSH Keys"
          initialValue={extraSshKeys.map((i) => i.value)}
          fetchData={async () => {
            const form = store.getState().form.instances[formId];
            const values: Mk8sInput = form.values as any;
            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "ssh_keys",
                cluster: inputToMk8s(values),
              },
            });
            return data.ssh_keys;
          }}
          multipleChoice
          onOk={(_value) => {
            const values: string[] = _value as any;
            const form = store.getState().form.instances[formId];
            const formValues: Mk8sInput = form.values as any;
            const _extraSshKeys = cloneDeep(formValues.digitalocean.extraSshKeys);

            for (const value of values) {
              if (_extraSshKeys.some((i) => i.value === value)) {
                continue;
              }
              _extraSshKeys.push({ id: uuidv4(), value: value });
            }
            formSetValue(formId, `digitalocean.extraSshKeys`, _extraSshKeys);
            formTriggerValidation(formId);
          }}
          onClose={() => setBrowserKey("")}
          labels={["Name", "ID"]}
          getData={(item: any) => [String(item.id), item.name, String(item.id)]}
        />
      ) : null}
      {browserKey === "reservedIps" ? (
        <NGProviderBrowser
          title="Browse Digital Ocean Reserved IPs"
          initialValue={reservedIps.map((i) => i.value)}
          fetchData={async () => {
            const form = store.getState().form.instances[formId];
            const values: Mk8sInput = form.values as any;

            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "reserved_ips",
                cluster: inputToMk8s(values),
              },
            });
            return data.reserved_ips;
          }}
          multipleChoice
          onOk={(_value) => {
            const values: string[] = _value as any;
            const form = store.getState().form.instances[formId];
            const formValues: Mk8sInput = form.values as any;
            const _reservedIps = cloneDeep(formValues.digitalocean.reservedIPs);

            for (const value of values) {
              if (_reservedIps.some((i) => i.value === value)) {
                continue;
              }
              _reservedIps.push({ id: uuidv4(), value: value });
            }
            formSetValue(formId, `digitalocean.reservedIPs`, _reservedIps);
            formTriggerValidation(formId);
          }}
          onClose={() => setBrowserKey("")}
          labels={["IP", "Attached To"]}
          getData={(item: any) => [item.ip, item.ip, item.droplet?.name]}
        />
      ) : null}
    </>
  );
};
